// The cables a room's system needs, as parts you buy — the System view's
// "Cables" rows and their lines in the order.
//
// Speaker wire used to be a row in the room checklist you could only tick
// (owner, 2026-09-27: "the only thing I can do is click it and it turns from
// red to a green check mark"). What the wire question actually has is an
// answer the owner cannot easily work out alone: which gauge, how much, and
// how long each run is — so it is a part now, bought like the speakers.
//
// Runs and gauges are README.md "Runs" / "Gauge by run" (runs.ts), the same
// maths the Buy list uses. One gauge is recommended for the whole system:
// the thickest any run needs, so a single spool serves every speaker and the
// longest run is never under-sized (the checklist row used to name the gauge
// covering the most metres, which could leave a long run on thinner wire).

import type { EditorDoc } from '@roomtreatment/core';
import type { Product } from '@roomtreatment/av-catalog';
import { buildContext } from './context';
import { runLength, runOrigin, wireGauge, type WireAwg } from './runs';
import type { BuildReport, Channel } from './types';

export interface WireRun {
  speakerId: string;
  /** The speaker's channel, when the build derived one. */
  channel: Channel | null;
  /** Origin to speaker, slack included, rounded up to 0.5 m. */
  lengthM: number;
  /** What this run alone needs. */
  gauge: WireAwg;
}

export interface CableNeeds {
  /** Every passive speaker's run; null with nothing to wire. */
  speakerWire: {
    /** The gauge to buy: the thickest any run needs. */
    gauge: WireAwg;
    totalM: number;
    longestM: number;
    runs: WireRun[];
  } | null;
  /** One cable per subwoofer from the receiver; null with none. */
  subCable: { count: number; longestM: number } | null;
}

export function cableNeeds(
  doc: EditorDoc,
  catalog: ReadonlyMap<string, Product>,
  report: Pick<BuildReport, 'channels'> | null = null,
): CableNeeds {
  const ctx = buildContext(doc, catalog, { hasRoomResults: false });
  const origin = runOrigin(ctx);

  const runs: WireRun[] = [];
  for (const id of [...ctx.bedIds, ...ctx.overheadIds]) {
    const entry = doc.speakers.find((s) => s.id === id);
    if (!entry) continue;
    const product = entry.productId ? catalog.get(entry.productId) : undefined;
    const specs = product?.specs.kind === 'speaker' ? product.specs : undefined;
    // An active speaker carries its own amplifier: it takes a power cord and
    // a signal lead, not speaker wire.
    if (specs?.active.v) continue;
    const lengthM = runLength(origin, entry.position);
    runs.push({
      speakerId: id,
      channel: report?.channels[id] ?? ctx.channels[id] ?? null,
      lengthM,
      gauge: wireGauge(specs?.impedanceOhm?.v, lengthM),
    });
  }

  const subLengths = ctx.subIds
    .map((id) => doc.speakers.find((s) => s.id === id))
    .filter((s): s is NonNullable<typeof s> => !!s)
    .map((s) => runLength(origin, s.position));

  return {
    speakerWire: runs.length === 0 ? null : {
      gauge: Math.min(...runs.map((r) => r.gauge)) as WireAwg,
      totalM: runs.reduce((t, r) => t + r.lengthM, 0),
      longestM: Math.max(...runs.map((r) => r.lengthM)),
      runs,
    },
    subCable: subLengths.length === 0 ? null : { count: subLengths.length, longestM: Math.max(...subLengths) },
  };
}
